/**
 * @module AEM
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
import { COMPONENT_MAP } from '../component-map.constants';
import { DynamicItem, NgAemComponent } from '../models/dynamic-item';
import { ContentItem } from './content-item.interface';
import { ContainerData } from '@core/aem/components/container/models/container-data.interface';
import { SETTINGS, CHANNELS } from '@environment/settings';

/**
 * Service for generating dynamic components
 * @access protected
 */
export class ContentResolver {
  /**
   * Generate a Dynamic Item Component for each provided component
   * @param comps
   * @returns {ContentComponent[]}
   */
  public static generateComponents(
    comps: ContentItem<ContainerData>[]
  ): ContentItem<ContainerData>[] {
    const isWeb = SETTINGS.CHANNEL.toLocaleLowerCase() === CHANNELS.WEB;
    for (let i: number = 0; i < comps.length; i++) {
      if (comps[i] && comps[i].hasOwnProperty('isWebContent')) {
        if (comps[i].isWebContent === isWeb) {
          this.genrateNestedComponents(comps[i]);
        }
      } else {
        this.genrateNestedComponents(comps[i]);
      }
    }
    return comps;
  }

  /**
   * Generated Nested Components (For Containers)
   * @param comps
   * @returns void
   */
  public static genrateNestedComponents(comp: ContentItem<ContainerData>): void {
    if (
      (comp.data && comp.data.items && comp.uiComponent === 'container-holder') ||
      comp.uiComponent === 'container'
    ) {
      comp.data.items = ContentResolver.generateComponents(comp.data.items);
    }
    if (comp && comp.uiComponent) {
      comp = ContentResolver.generateComponent(comp);
    }
  }

  /**
   * Generate a Dynamic Item Component for a component
   * @param comp
   * @returns {ContentComponent}
   */
  public static generateComponent(comp: ContentItem<ContainerData>): ContentItem<ContainerData> {
    const rawComponent: ContentItem<ContainerData> = comp;
    const compClass: NgAemComponent = ContentResolver.getComponentClass(rawComponent.uiComponent);
    comp.dynamicItem = new DynamicItem(compClass, rawComponent.data);
    return comp;
  }

  /**
   * Get the component class based on a provided id
   * @param id
   * @returns {any}
   */
  private static getComponentClass(id: string): NgAemComponent {
    if (COMPONENT_MAP[id]) {
      return COMPONENT_MAP[id];
    }
    throw new Error(`Component of id: ${id}, does not exist`);
  }
}
